import React, { FC, useMemo } from 'react';
import { Heading2, Text } from '@blueprint/ui';

import { ArticlesForHomepageQuery } from '../../queries/propertyNews.generated';
import {
    PropertyNewsWrapper,
    ArticleSmall,
    ArticleLarge,
    ImageContainer,
    ArticleWrapper,
    StyledHeading,
    MainArticleInfoWrapper,
    MainImageHeading,
    StyledImage,
    MainArticleSnippet,
    MainText,
    HeadingWrapper,
    StyledButton,
    StyledButtonMobile,
    MainArticleWrapperWithFocus,
    ArticleSmallWithFocusRing,
    LinkFocus,
    StyledSmallArticleImage,
    MainImageContainer
} from './PropertyNews.styles';
import homepageTranslations from 'public/locales/en/homepage.json';
import { buildDiscoverLink, formatDate } from '../../../shared/helpers';
import { MAIN_SITE_LINK } from '../../../constants';
import { redirect } from '../../../shared/helpers';
import { getTranslator } from '../../../shared/i18n/getTranslator';
import {
    trackAllPropertyNewsCTA,
    trackIndividualArticle,
    trackMainArticle
} from './PropertyNews.tracking';

const t = getTranslator(homepageTranslations);

const mainHeadlinePropertyNewsText = t('property_news.headline');
const propertyNewsText = t('property_news.property_new_copy');
const seeAllPropertyNewsCTA = t('property_news.see_all_property_news_cta');

const formatArticles = (articles: ArticlesForHomepageQuery['articles']) => {
    const { items = [] } = articles || {};

    return useMemo(() => {
        return items.map((article) => {
            const {
                heroImage,
                title,
                category,
                slug,
                publicationDateAndTime,
                snippet
            } = article || {};

            return {
                imageUrl: heroImage?.url,
                title,
                articleLink: buildDiscoverLink(category?.slug, slug),
                rawDateTime: publicationDateAndTime,
                displayDateTime: formatDate(publicationDateAndTime || ''),
                snippet
            };
        });
    }, [items]);
};

type PropertyNewsProps = {
    articles: ArticlesForHomepageQuery['articles'];
};

export const PropertyNews: FC<PropertyNewsProps> = ({ articles }) => {
    const formattedArticles = formatArticles(articles);

    const firstArticle = formattedArticles[0];
    const remainingArticles = formattedArticles.slice(1);

    if (!firstArticle) {
        return null;
    }

    const discoverLink = `${MAIN_SITE_LINK}/discover/property-news/`;
    const buildDiscoverSectionLink = () => {
        redirect(discoverLink);
    };

    return (
        <>
            <PropertyNewsWrapper>
                <HeadingWrapper>
                    <Heading2 size="3">{mainHeadlinePropertyNewsText}</Heading2>
                    <StyledButton
                        href={discoverLink}
                        variant="tertiary"
                        onClick={() => {
                            buildDiscoverSectionLink();
                            trackAllPropertyNewsCTA();
                        }}
                        data-testid="see-all-property-news-cta-desktop"
                    >
                        {seeAllPropertyNewsCTA}
                    </StyledButton>
                </HeadingWrapper>
                <MainText size="7">{propertyNewsText}</MainText>
                <LinkFocus
                    plain
                    title={firstArticle?.title}
                    href={firstArticle.articleLink}
                    data-testid={`article-link-1`}
                    onClick={() => {
                        trackMainArticle(firstArticle?.title);
                    }}
                >
                    <ArticleLarge data-testid="main-property-news-article">
                        <MainArticleWrapperWithFocus>
                            <MainImageContainer>
                                <StyledImage
                                    alt={`${firstArticle?.title} Image`}
                                    src={`${firstArticle?.imageUrl}?w=660&h=220`}
                                    lazy={true}
                                />
                            </MainImageContainer>
                            <MainArticleInfoWrapper>
                                <Text size="8">
                                    <time
                                        dateTime={firstArticle.rawDateTime}
                                        data-testid={`ArticlePublishedData-1`}
                                    >
                                        {firstArticle.displayDateTime}
                                    </time>
                                </Text>
                                <MainImageHeading size="5">
                                    {firstArticle?.title}
                                </MainImageHeading>
                                <MainArticleSnippet size="8">
                                    {firstArticle?.snippet}
                                </MainArticleSnippet>
                            </MainArticleInfoWrapper>
                        </MainArticleWrapperWithFocus>
                    </ArticleLarge>
                </LinkFocus>
                <ArticleWrapper>
                    {remainingArticles.map((article, index) => {
                        const {
                            imageUrl,
                            title,
                            articleLink,
                            rawDateTime,
                            displayDateTime
                        } = article || {};

                        return (
                            <ArticleSmall key={`${index}-${title}`}>
                                <ArticleSmallWithFocusRing
                                    key={`${index}-${title}`}
                                    data-testid={`main-property-news-article-${index}`}
                                >
                                    <LinkFocus
                                        plain
                                        title={title}
                                        href={articleLink}
                                        data-testid={`article-link-1`}
                                        onClick={() => {
                                            trackIndividualArticle(title);
                                        }}
                                    >
                                        <ImageContainer>
                                            <noscript>
                                                <img src={`${imageUrl}`} />
                                            </noscript>
                                            <StyledSmallArticleImage
                                                alt={`${title} Image`}
                                                src={`${imageUrl}?w=320&h=250`}
                                                lazy={false}
                                            />
                                        </ImageContainer>
                                        <Text>
                                            <time
                                                dateTime={rawDateTime}
                                                data-testid={`ArticlePublishedData-1`}
                                            >
                                                {displayDateTime}
                                            </time>
                                        </Text>
                                        <StyledHeading size="7">
                                            {title}
                                        </StyledHeading>
                                    </LinkFocus>
                                </ArticleSmallWithFocusRing>
                            </ArticleSmall>
                        );
                    })}
                </ArticleWrapper>
                <StyledButtonMobile
                    href={discoverLink}
                    variant="tertiary"
                    onClick={buildDiscoverSectionLink}
                    data-testid="see-all-property-news-cta-mobile"
                    isFullWidth
                >
                    {seeAllPropertyNewsCTA}
                </StyledButtonMobile>
            </PropertyNewsWrapper>
        </>
    );
};
